import React from "react";
import { useTranslation } from "react-i18next";

const relationName = (entity, fallback) =>
  entity?.familyName ||
  [entity?.firstName, entity?.lastName].filter(Boolean).join(" ").trim() ||
  fallback;

const RelationGroup = ({ title, families = [], children = [] }) => {
  const { t } = useTranslation();

  return (
    <section className="rounded-xl border border-gray-200 bg-white/90 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/80">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>

      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {t("parentMerge.relations.families", { defaultValue: "Families" })}
        </h4>
        {families.length ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-200">
            {families.map((family, index) => (
              <li key={family?.id || index}>
                {relationName(
                  family,
                  t("parentMerge.relations.unnamedFamily", { defaultValue: "Unnamed family" })
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
            {t("parentMerge.relations.noFamilies", { defaultValue: "No linked families" })}
          </p>
        )}
      </div>

      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {t("parentMerge.relations.children", { defaultValue: "Children" })}
        </h4>
        {children.length ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-200">
            {children.map((child, index) => (
              <li key={child?.id || index}>
                {relationName(
                  child,
                  t("parentMerge.relations.unnamedChild", { defaultValue: "Unnamed child" })
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
            {t("parentMerge.relations.noChildren", { defaultValue: "No linked children" })}
          </p>
        )}
      </div>
    </section>
  );
};

const RelationsOverviewStep = ({ targetRelations = {}, sourceRelations = {} }) => {
  const { t } = useTranslation();

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {t("parentMerge.relations.title", { defaultValue: "Relations overview" })}
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        {t("parentMerge.relations.description", {
          defaultValue: "Review the existing relations that the backend merge will preserve.",
        })}
      </p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <RelationGroup
          title={t("parentMerge.parentA", { defaultValue: "Parent A" })}
          families={targetRelations.families}
          children={targetRelations.children}
        />
        <RelationGroup
          title={t("parentMerge.parentB", { defaultValue: "Parent B" })}
          families={sourceRelations.families}
          children={sourceRelations.children}
        />
      </div>
    </div>
  );
};

export default RelationsOverviewStep;
